import { useEffect, useMemo } from 'react';
import { generatePath, useParams } from 'react-router-dom';
import { Dictionary, IHomepageMainNavItems } from "../../../types/working-model";

interface RouteMapProps {
	eventMainNavItems?: IHomepageMainNavItems[];
	debug?: boolean;
}

// PLEASE NOTE - when adding new routes, please add new variables
// to the CommonParams and getParams definitions in utils/redirects.ts
export const DebugDefaultRouteMap = {
	CustomPage: "/event/:uuid/:language/pages/:customPath",
	Session: "/event/:uuid/:language/session/:sessionUuid",
	Fireside: "/event/:uuid/:language/session/:sessionUuid/fireside",
	FiresideNoHomepage: "/event/:uuid/:language/fireside",
	Breakout: "/event/:uuid/:language/session/:sessionUuid/breakout/:breakoutUuid",
	Registration: "/event/:uuid/:language/registration",
	PrivacyPolicy: "/event/:uuid/:language/privacy-policy",
	TermsConditions: "/event/:uuid/:language/terms-conditions",
	Home: `/event/:uuid/:language/home`,
	Sessions: `/event/:uuid/:language/sessions`,
	People: `/event/:uuid/:language/people`,
	Directory: '/event/:uuid/:language/directory',
	Profile: "/event/:uuid/:language/profile",
	Watchlist: "/event/:uuid/:language/watchlist",
	AttendeeProfile: "/event/:uuid/:language/profile/:attendeeId",
	Landing: `/event/:uuid/:language`,
	LandingRedirect: "/event/:uuid",
	Leaderboard: `/event/:uuid/:language/leaderboard`,

	// there's no route map that uses these, please figure out why they exist and remove them, it's a dead-end
	SingleSession: "/event/:uuid/:language",
	AdminSingleSession: "/admin/events/create/:id/event/:language?",
};

// Relative paths for use inside <Routes> nested under /event/:uuid/:language?/*
// DebugDefaultRouteMap (absolute) is still used for matchPath checks against the full URL
export const DebugRelativeRouteMap = {
	CustomPage: "pages/:customPath",
	Session: "session/:sessionUuid",
	Fireside: "session/:sessionUuid/fireside",
	FiresideNoHomepage: "fireside",
	Breakout: "session/:sessionUuid/breakout/:breakoutUuid",
	Registration: "registration",
	PrivacyPolicy: "privacy-policy",
	TermsConditions: "terms-conditions",
	Home: "home",
	Sessions: "sessions",
	People: "people",
	Directory: "directory",
	Profile: "profile",
	Watchlist: "watchlist",
	AttendeeProfile: "profile/:attendeeId",
	Leaderboard: "leaderboard",
	AdminSingleSession: "/admin/events/create/:id/event/:language?",
};

export const DefaultRouteMap = {
	CustomPage: "/:eventName/:language/pages/:customPath",
	Session: "/:eventName/:language/session/:sessionUuid",
	Breakout: "/:eventName/:language/session/:sessionUuid/breakout/:breakoutUuid",
	Fireside: "/:eventName/:language/session/:sessionUuid/fireside",
	FiresideNoHomepage: "/:eventName/:language/fireside",
	Registration: "/:eventName/:language/registration/:sessionUuid?",
	PrivacyPolicy: "/:eventName/:language/privacy-policy",
	TermsConditions: "/:eventName/:language/terms-conditions",
	Home: `/:eventName/:language/home`,
	Sessions: `/:eventName/:language/sessions`,
	People: `/:eventName/:language/people`,
	Directory: `/:eventName/:language/directory`,
	Profile: "/:eventName/:language/profile",
	Watchlist: "/:eventName/:language/watchlist",
	AttendeeProfile: "/:eventName/:language/profile/:attendeeId",
	Landing: `/:eventName/:language`,
	LandingRedirect: "/:eventName",
	Leaderboard: `/:eventName/:language/leaderboard`
};

class Linker {
	debug = false;
	params: Record<string, string> = {};

	getLink = (path: keyof typeof DefaultRouteMap, params?: Record<string, string>) => {
		if (this.debug) {
			return generatePath(DebugDefaultRouteMap[path], params || this.params);
		} else {
			return generatePath(DefaultRouteMap[path], params || this.params);
		}
	};

	setDebug = (debug: boolean) => {
		this.debug = debug;
	};

	setParams = (params: Record<string, string>) => {
		this.params = params;
	};
}

export const linker = new Linker();

/**
 * Absolute route patterns, for matchPath/generatePath checks against a full URL.
 *
 * useRouteMap returns RELATIVE patterns because <Route path> inside the nested
 * <Routes> requires them. Those can never match a full pathname - react-router
 * anchors every pattern with a leading slash - so anything comparing against
 * location.pathname must use this hook instead.
 */
export function useAbsoluteRouteMap({ eventMainNavItems, debug }: RouteMapProps): Dictionary {
	return useMemo(() => {
		const prefix = debug ? '/event/:uuid' : '/:eventName';
		const withPrefix = (path: string) => `${prefix}/:language/${path}`;

		const customRouteMap = eventMainNavItems?.reduce((a: { [key: string]: string }, c: IHomepageMainNavItems) => {
			a[c.name] = withPrefix(c.name.toLowerCase());
			return a;
		}, {});

		if (!customRouteMap) {
			return debug ? DebugDefaultRouteMap : DefaultRouteMap;
		}

		// keep in sync with the extra keys added to the relative map below - useLocationEvents
		// iterates every value, so a missing key means that page is never tracked
		customRouteMap.Profile = withPrefix('profile');
		customRouteMap.Watchlist = withPrefix('watchlist');
		customRouteMap.AttendeeProfile = withPrefix('profile/:attendeeId');
		customRouteMap.Directory = withPrefix('directory');
		customRouteMap.CustomPage = withPrefix('pages/:customPath');
		customRouteMap.Session = withPrefix('session/:sessionUuid');
		customRouteMap.Registration = withPrefix('registration');
		customRouteMap.Fireside = withPrefix('session/:sessionUuid/fireside');
		customRouteMap.FiresideNoHomepage = withPrefix('fireside');
		customRouteMap.Breakout = withPrefix('session/:sessionUuid/breakout/:breakoutUuid');
		customRouteMap.PrivacyPolicy = withPrefix('privacy-policy');
		customRouteMap.TermsConditions = withPrefix('terms-conditions');
		customRouteMap.Leaderboard = withPrefix('leaderboard');

		return customRouteMap;
	}, [debug, eventMainNavItems]);
}

export default function useRouteMap({ eventMainNavItems, debug }: RouteMapProps): Dictionary {
	const params = useParams() as unknown as Record<string, string>;

	useEffect(() => {
		linker.setDebug(debug || false);
		linker.setParams(params);
	}, [debug, params]);


	return useMemo(() => {
		let routeMap: Dictionary = {};

		// TODO: Post MVP when we do custom tab names decide if we want to use custom_name or
		// name for the endpoint. if custom_name, update marketing-nav to use that as well.
		const customRouteMap = eventMainNavItems?.reduce((a: { [key: string]: string }, c: IHomepageMainNavItems) => {
			a[c.name] = c.name.toLowerCase();
			return a;
		}, {});

		if (customRouteMap) {
			customRouteMap.Profile = "profile";
			customRouteMap.Watchlist = "watchlist";
			customRouteMap.AttendeeProfile = "profile/:attendeeId";
			customRouteMap.Directory = "directory";
			customRouteMap.CustomPage = "pages/:customPath";
			// Routes not covered by nav items but still needed for direct navigation
			customRouteMap.Session = "session/:sessionUuid";
			customRouteMap.Registration = "registration";
			customRouteMap.Fireside = "session/:sessionUuid/fireside";
			customRouteMap.FiresideNoHomepage = "fireside";
			customRouteMap.Breakout = "session/:sessionUuid/breakout/:breakoutUuid";
			customRouteMap.PrivacyPolicy = "privacy-policy";
			customRouteMap.TermsConditions = "terms-conditions";
			customRouteMap.Leaderboard = "leaderboard";

			routeMap = customRouteMap;
		} else {
			routeMap = DebugRelativeRouteMap;
		}

		return routeMap;
	}, [eventMainNavItems]);
}
